"use client";

import {NeoButton} from "@/components/forms";
import {NeoSlider} from "@/components/shared";
import {fmtTime} from "@/libs/helper/util";
import {AnnouncementService} from "@/service/announcement.service";
import {liveService} from "@/service/live.service";
import {userService} from "@/service/users.service";
import {Tab, Tabs} from "@nextui-org/react";
import {
  GoogleMap,
  Marker,
  OverlayView,
  Polyline,
  useJsApiLoader
} from "@react-google-maps/api";
import {useCallback, useEffect, useRef, useState} from "react";
import Select from "react-select";

// ─── Google Maps API key (same env var as map-view.tsx) ──────────────────────
const mapKey: string = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";

// ─── Types ────────────────────────────────────────────────────────────────────

type LatLng = {lat: number; lng: number};
type RefRoutePoint = LatLng & {ti?: number};

// Non-editable Polylines never populate PolyMouseEvent's vertex/edge fields,
// so the clicked route point has to be resolved from the click's latLng instead.
const findNearestRoutePoint = (
  points: RefRoutePoint[],
  latLng: google.maps.LatLng
): RefRoutePoint | null => {
  if (!points.length) return null;
  const clickLat = latLng.lat();
  const clickLng = latLng.lng();
  let nearest = points[0];
  let nearestDist = Infinity;
  for (const point of points) {
    const dLat = point.lat - clickLat;
    const dLng = point.lng - clickLng;
    const dist = dLat * dLat + dLng * dLng;
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = point;
    }
  }
  return nearest;
};
type SelectOption = {value: string; label: string};
type ChangeType = "pickup" | "drop" | "both";
type LocationType = "existing" | "custom";

type NearbyVehicle = {
  trackerId: string;
  trackerName: string;
  regNo: string;
  routeId: number;
  routeName: string;
  routeStart: number;
  routeEnd: number;
  bpReachedTime: number;
};

export type ChangePickupDropModalData = {
  client: Client;
  items: UserDetailsData;
  userId: string;
  onClose: () => void;
  onRefresh?: () => Promise<void>;
  routeOptions: SelectOption[];
  vehicleOptions: SelectOption[];
  boardingPointOptions: SelectOption[];
};

type VehicleRoute = {
  routeId: number;
  routeName: string;
  type: number; // 0 = morning/pickup, 1 = evening/drop
  startTime: number;
  endTime: number;
  activeOnDays: string;
  bpName: string;
  bpLat: number;
  bpLng: number;
  bpTime: number;
};

type VehicleWithBps = {
  id: string;
  name: string;
  clientId: string;
  vehicleTripType: number;
  routes: VehicleRoute[];
};

type AnnouncementRoute = {
  routeId: number;
  routeName?: string;
  tripType?: number;
};
type AnnouncementVehicle = {
  trackerId: string;
  trackerName: string;
  routes: AnnouncementRoute[];
};

type RefBoarding = {
  lat: number;
  lon: number;
  type: number;
  bpId: number;
  bpName: string;
  users: any[];
};

// ─── Mock vehicle options (replace with real data) ───────────────────────────

const MOCK_VEHICLE_OPTIONS: SelectOption[] = [];

// ─── Custom SVG marker factory ────────────────────────────────────────────────

const makePinSvg = (fill: string, label: string) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
      <path d="M18 0C8.059 0 0 8.059 0 18c0 12.902 16.364 24.928 17.07 25.437a1.5 1.5 0 0 0 1.86 0C19.636 42.928 36 30.902 36 18 36 8.059 27.941 0 18 0z"
            fill="${fill}"/>
      <circle cx="18" cy="18" r="9" fill="white" opacity="0.9"/>
      <text x="18" y="23" font-family="Arial,sans-serif" font-size="10" font-weight="bold"
            fill="${fill}" text-anchor="middle">${label}</text>
    </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const makeCurrentBpSvg = (fill: string) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
    <circle cx="17" cy="17" r="15" fill="${fill}" stroke="white" stroke-width="3"/>
    <circle cx="17" cy="17" r="9" fill="white"/>
    <circle cx="17" cy="17" r="4" fill="${fill}"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// ─── Map styles (light – copied from map-view.tsx) ────────────────────────────

const MAP_STYLES: google.maps.MapTypeStyle[] = [
  {
    featureType: "all",
    elementType: "labels.text.fill",
    stylers: [{color: "#7c93a3"}, {lightness: "-10"}]
  },
  {
    featureType: "administrative.country",
    elementType: "geometry",
    stylers: [{visibility: "on"}]
  },
  {
    featureType: "administrative.country",
    elementType: "geometry.stroke",
    stylers: [{color: "#c2d1d6"}]
  },
  {
    featureType: "landscape",
    elementType: "geometry.fill",
    stylers: [{color: "#dde3e3"}]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.fill",
    stylers: [{color: "#c2d1d6"}]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{color: "#a9b4b8"}, {lightness: "0"}]
  },
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [{color: "#a3c7df"}]
  }
];

// ─── BP Info Card (custom overlay — avoids InfoWindow height clipping) ────────

const BpInfoCard = ({
  bp,
  tabType,
  onClose,
  onSelect
}: {
  bp: RefBoarding;
  tabType: "pickup" | "drop";
  onClose: () => void;
  onSelect: () => void;
}) => {
  const accent = tabType === "pickup" ? "#3b82f6" : "#f97316";
  return (
    <div
      style={{
        position: "absolute",
        transform: "translate(-50%, calc(-100% - 44px))",
        background: "#ffffff",
        borderRadius: 10,
        boxShadow: "0 6px 24px rgba(0,0,0,0.16)",
        width: 224,
        padding: "12px 12px 14px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
        pointerEvents: "auto",
        zIndex: 9999,
        boxSizing: "border-box" as const
      }}
    >
      {/* Close */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#9ca3af",
          fontSize: 18,
          lineHeight: 1,
          padding: "0 2px"
        }}
      >
        ×
      </button>

      {/* Accent bar + name */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          marginBottom: 10,
          paddingRight: 20
        }}
      >
        <div
          style={{
            width: 3,
            minHeight: 16,
            borderRadius: 99,
            flexShrink: 0,
            marginTop: 2,
            background: accent
          }}
        />
        <span
          style={{
            fontWeight: 700,
            fontSize: 13,
            color: "#111827",
            lineHeight: 1.35,
            wordBreak: "break-word"
          }}
        >
          {bp.bpName}
        </span>
      </div>

      {/* Divider */}
      <div style={{height: 1, background: "#f3f4f6", marginBottom: 10}} />

      {/* Badges */}
      <div style={{display: "flex", gap: 6, marginBottom: 12}}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 999,
            padding: "4px 10px",
            fontSize: 11,
            color: "#4b5563",
            fontWeight: 500,
            whiteSpace: "nowrap"
          }}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6b7280"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          07:30 AM
        </span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 999,
            padding: "4px 10px",
            fontSize: 11,
            color: "#4b5563",
            fontWeight: 500,
            whiteSpace: "nowrap"
          }}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6b7280"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          {bp.users?.length ?? 0} users
        </span>
      </div>

      {/* Button */}
      <button
        onClick={onSelect}
        style={{
          display: "block",
          width: "100%",
          padding: "8px 0",
          borderRadius: 8,
          border: "none",
          background: accent,
          color: "white",
          fontWeight: 600,
          fontSize: 12,
          cursor: "pointer",
          letterSpacing: "0.02em"
        }}
      >
        Use this BP
      </button>

      {/* Tail */}
      <div
        style={{
          position: "absolute",
          bottom: -8,
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "8px solid transparent",
          borderRight: "8px solid transparent",
          borderTop: "8px solid #ffffff"
        }}
      />
    </div>
  );
};

// ─── Custom position info card ────────────────────────────────────────────────

const CustomPosCard = ({
  pos,
  tabType,
  onClose,
  onSearch
}: {
  pos: LatLng;
  tabType: "pickup" | "drop";
  onClose: () => void;
  onSearch: () => void;
}) => {
  const accent = tabType === "pickup" ? "#3b82f6" : "#f97316";
  return (
    <div
      style={{
        position: "absolute",
        transform: "translate(-50%, calc(-100% - 44px))",
        background: "#ffffff",
        borderRadius: 10,
        boxShadow: "0 6px 24px rgba(0,0,0,0.16)",
        width: 196,
        padding: "12px 12px 14px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
        pointerEvents: "auto",
        zIndex: 9999,
        boxSizing: "border-box" as const
      }}
    >
      {/* Close */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#9ca3af",
          fontSize: 18,
          lineHeight: 1,
          padding: "0 2px"
        }}
      >
        ×
      </button>

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 10,
          paddingRight: 20
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: accent,
            flexShrink: 0
          }}
        />
        <span style={{fontWeight: 700, fontSize: 12, color: "#374151"}}>
          {tabType === "pickup" ? "Pickup" : "Drop"} Location
        </span>
      </div>

      {/* Divider */}
      <div style={{height: 1, background: "#f3f4f6", marginBottom: 10}} />

      {/* Coordinates */}
      <div style={{marginBottom: 12}}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 5
          }}
        >
          <span style={{fontSize: 11, color: "#6b7280"}}>Lat</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#111827",
              fontFamily: "monospace"
            }}
          >
            {pos.lat.toFixed(6)}
          </span>
        </div>
        <div style={{display: "flex", justifyContent: "space-between"}}>
          <span style={{fontSize: 11, color: "#6b7280"}}>Lng</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#111827",
              fontFamily: "monospace"
            }}
          >
            {pos.lng.toFixed(6)}
          </span>
        </div>
      </div>

      {/* Search button */}
      <button
        onClick={onSearch}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          width: "100%",
          padding: "8px 0",
          borderRadius: 8,
          border: "none",
          background: accent,
          color: "white",
          fontWeight: 600,
          fontSize: 12,
          cursor: "pointer"
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        Search Vehicles
      </button>

      {/* Tail */}
      <div
        style={{
          position: "absolute",
          bottom: -8,
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "8px solid transparent",
          borderRight: "8px solid transparent",
          borderTop: "8px solid #ffffff"
        }}
      />
    </div>
  );
};

// ─── Route point info card (click on ref-route polyline) ─────────────────────

const RoutePointInfoCard = ({
  point,
  tabType,
  onClose,
  onUse
}: {
  point: RefRoutePoint;
  tabType: "pickup" | "drop";
  onClose: () => void;
  onUse: () => void;
}) => {
  const accent = tabType === "pickup" ? "#3b82f6" : "#f97316";
  return (
    <div
      style={{
        position: "absolute",
        transform: "translate(-50%, calc(-100% - 14px))",
        background: "#ffffff",
        borderRadius: 10,
        boxShadow: "0 6px 24px rgba(0,0,0,0.16)",
        width: 196,
        padding: "12px 12px 14px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
        pointerEvents: "auto",
        zIndex: 9999,
        boxSizing: "border-box" as const
      }}
    >
      {/* Close */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#9ca3af",
          fontSize: 18,
          lineHeight: 1,
          padding: "0 2px"
        }}
      >
        ×
      </button>

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 10,
          paddingRight: 20
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: accent,
            flexShrink: 0
          }}
        />
        <span style={{fontWeight: 700, fontSize: 12, color: "#374151"}}>
          {tabType === "pickup" ? "Pickup" : "Drop"} Route Point
        </span>
      </div>

      {/* Divider */}
      <div style={{height: 1, background: "#f3f4f6", marginBottom: 10}} />

      {/* Details */}
      <div style={{marginBottom: 12}}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 5
          }}
        >
          <span style={{fontSize: 11, color: "#6b7280"}}>Time</span>
          <span style={{fontSize: 11, fontWeight: 600, color: "#111827"}}>
            {fmtTime(point.ti)}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 5
          }}
        >
          <span style={{fontSize: 11, color: "#6b7280"}}>Lat</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#111827",
              fontFamily: "monospace"
            }}
          >
            {point.lat.toFixed(6)}
          </span>
        </div>
        <div style={{display: "flex", justifyContent: "space-between"}}>
          <span style={{fontSize: 11, color: "#6b7280"}}>Lng</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#111827",
              fontFamily: "monospace"
            }}
          >
            {point.lng.toFixed(6)}
          </span>
        </div>
      </div>

      {/* Use this location button */}
      <button
        onClick={onUse}
        style={{
          display: "block",
          width: "100%",
          padding: "8px 0",
          borderRadius: 8,
          border: "none",
          background: accent,
          color: "white",
          fontWeight: 600,
          fontSize: 12,
          cursor: "pointer",
          letterSpacing: "0.02em"
        }}
      >
        Use this Location
      </button>

      {/* Tail */}
      <div
        style={{
          position: "absolute",
          bottom: -8,
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "8px solid transparent",
          borderRight: "8px solid transparent",
          borderTop: "8px solid #ffffff"
        }}
      />
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StepBadge = ({number, color}: {number: number; color: string}) => (
  <div
    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
    style={{background: color}}
  >
    {number}
  </div>
);

const SectionLabel = ({
  children,
  color
}: {
  children: React.ReactNode;
  color: string;
}) => (
  <h4 className="text-xs font-semibold leading-tight" style={{color}}>
    {children}
  </h4>
);

const FieldLabel = ({
  children,
  required
}: {
  children: React.ReactNode;
  required?: boolean;
}) => (
  <label className="mb-1 block text-xs font-medium text-gray-600">
    {children}
    {required && <span className="ml-0.5 text-red-500">*</span>}
  </label>
);

const selectStyles = {
  control: (base: any) => ({
    ...base,
    borderRadius: 8,
    borderColor: "#e5e7eb",
    minHeight: 34,
    fontSize: 12,
    boxShadow: "none",
    "&:hover": {borderColor: "#9ca3af"}
  }),
  menu: (base: any) => ({...base, fontSize: 12, zIndex: 9999}),
  menuPortal: (base: any) => ({...base, zIndex: 10000}),
  option: (base: any, state: any) => ({
    ...base,
    background: state.isSelected
      ? "#16a34a"
      : state.isFocused
        ? "#f0fdf4"
        : "white",
    color: state.isSelected ? "white" : "#111827"
  }),
  valueContainer: (base: any) => ({...base, padding: "0 8px"}),
  dropdownIndicator: (base: any) => ({...base, padding: "0 6px"})
};

const MAP_CENTER: LatLng = {lat: 11.025, lng: 76.972};

// ─── Google Map section ───────────────────────────────────────────────────────

type MapSectionProps = {
  showPickup: boolean;
  showDrop: boolean;
  newPickupPos: LatLng | null;
  newDropPos: LatLng | null;
  pickupRefRoute?: RefRoutePoint[];
  dropRefRoute?: RefRoutePoint[];
  pickupBoardings?: RefBoarding[];
  dropBoardings?: RefBoarding[];
  currentPickupBp?: LatLng | null;
  currentDropBp?: LatLng | null;
  initialCenter?: LatLng | null;
  pinPickMode?: boolean;
  pinPickPos?: LatLng | null;
  pinPickColor?: string;
  onMapClick?: (pos: LatLng) => void;
  onPinDrag?: (pos: LatLng) => void;
  onSelectBp?: (bp: RefBoarding, tabType: "pickup" | "drop") => void;
  onPickupPosDrag?: (pos: LatLng) => void;
  onDropPosDrag?: (pos: LatLng) => void;
  onSearchFromPos?: (pos: LatLng, tabType: "pickup" | "drop") => void;
  onUseRoutePoint?: (pos: LatLng, tabType: "pickup" | "drop") => void;
};

const MapSection = ({
  showPickup,
  showDrop,
  newPickupPos,
  newDropPos,
  pickupRefRoute = [],
  dropRefRoute = [],
  pickupBoardings = [],
  dropBoardings = [],
  currentPickupBp = null,
  currentDropBp = null,
  initialCenter = null,
  pinPickMode = false,
  pinPickPos = null,
  pinPickColor = "#3b82f6",
  onMapClick,
  onPinDrag,
  onSelectBp,
  onPickupPosDrag,
  onDropPosDrag,
  onSearchFromPos,
  onUseRoutePoint
}: MapSectionProps) => {
  const {isLoaded} = useJsApiLoader({googleMapsApiKey: mapKey});
  const mapRef = useRef<google.maps.Map | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const hasCenteredRef = useRef(false);
  const [activeInfo, setActiveInfo] = useState<{
    bp: RefBoarding;
    tabType: "pickup" | "drop";
  } | null>(null);
  const [activeCustomInfo, setActiveCustomInfo] = useState<{
    pos: LatLng;
    tabType: "pickup" | "drop";
  } | null>(null);
  const [activeRoutePointInfo, setActiveRoutePointInfo] = useState<{
    point: RefRoutePoint;
    tabType: "pickup" | "drop";
  } | null>(null);
  const onLoad = useCallback((m: google.maps.Map) => {
    mapRef.current = m;
    setMap(m);
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
    setMap(null);
  }, []);

  // Center on boarding point once when it first becomes available
  useEffect(() => {
    if (!map || !initialCenter || hasCenteredRef.current) return;
    map.setCenter(initialCenter);
    map.setZoom(15);
    hasCenteredRef.current = true;
  }, [map, initialCenter]);

  // Refit bounds only when route data or custom positions change — NOT on marker click
  useEffect(() => {
    if (!map) return;
    const allPoints = [...pickupRefRoute, ...dropRefRoute];
    if (allPoints.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    allPoints.forEach(p => bounds.extend(p));
    if (newPickupPos) bounds.extend(newPickupPos);
    if (newDropPos) bounds.extend(newDropPos);
    map.fitBounds(bounds, 60);
  }, [map, pickupRefRoute, dropRefRoute, newPickupPos, newDropPos]);

  if (!isLoaded) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-2 text-gray-400">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3" />
            <path
              d="M12 2a10 10 0 0 1 10 10"
              stroke="#16a34a"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
          <span className="text-xs">Loading map…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <GoogleMap
        mapContainerStyle={{width: "100%", height: "100%"}}
        center={MAP_CENTER}
        zoom={13}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={e => {
          if (pinPickMode && e.latLng && onMapClick) {
            onMapClick({lat: e.latLng.lat(), lng: e.latLng.lng()});
          }
        }}
        options={{
          styles: MAP_STYLES,
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          draggableCursor: pinPickMode ? "crosshair" : undefined
        }}
      >
        {/* ── Reference route polylines (from API) ── */}
        {/* {showPickup && ( */}
        <Polyline
          path={pickupRefRoute}
          options={{
            strokeColor: "#3b82f6",
            strokeWeight: 3,
            strokeOpacity: 0.9
          }}
        />
        {/* Invisible wide hit-target so clicking near the thin line registers */}
        <Polyline
          path={pickupRefRoute}
          options={{
            strokeColor: "#3b82f6",
            strokeWeight: 18,
            strokeOpacity: 0,
            zIndex: 10
          }}
          onClick={e => {
            if (!e.latLng) return;
            const point = findNearestRoutePoint(pickupRefRoute, e.latLng);
            if (point) {
              setActiveCustomInfo(null);
              setActiveInfo(null);
              setActiveRoutePointInfo({point, tabType: "pickup"});
            }
          }}
        />

        <Polyline
          path={dropRefRoute}
          options={{
            strokeColor: "#f97316",
            strokeWeight: 3,
            strokeOpacity: 0.9
          }}
        />
        {/* Invisible wide hit-target so clicking near the thin line registers */}
        <Polyline
          path={dropRefRoute}
          options={{
            strokeColor: "#f97316",
            strokeWeight: 18,
            strokeOpacity: 0,
            zIndex: 10
          }}
          onClick={e => {
            if (!e.latLng) return;
            const point = findNearestRoutePoint(dropRefRoute, e.latLng);
            if (point) {
              setActiveCustomInfo(null);
              setActiveInfo(null);
              setActiveRoutePointInfo({point, tabType: "drop"});
            }
          }}
        />

        {/* ── Route point info card (click on ref-route polyline) ── */}
        {activeRoutePointInfo && (
          <OverlayView
            position={{
              lat: activeRoutePointInfo.point.lat,
              lng: activeRoutePointInfo.point.lng
            }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <RoutePointInfoCard
              point={activeRoutePointInfo.point}
              tabType={activeRoutePointInfo.tabType}
              onClose={() => setActiveRoutePointInfo(null)}
              onUse={() => {
                onUseRoutePoint?.(
                  {
                    lat: activeRoutePointInfo.point.lat,
                    lng: activeRoutePointInfo.point.lng
                  },
                  activeRoutePointInfo.tabType
                );
                setActiveRoutePointInfo(null);
              }}
            />
          </OverlayView>
        )}

        {/* ── Boarding point markers ── */}
        {showPickup &&
          pickupBoardings.map(b => (
            <Marker
              key={`pb-${b.bpId}`}
              position={{lat: b.lat, lng: b.lon}}
              icon={{
                url: makePinSvg("#3b82f6", "P"),
                scaledSize: new window.google.maps.Size(28, 36),
                anchor: new window.google.maps.Point(14, 36)
              }}
              title={b.bpName}
              onClick={() => setActiveInfo({bp: b, tabType: "pickup"})}
            />
          ))}
        {showDrop &&
          dropBoardings.map(b => (
            <Marker
              key={`db-${b.bpId}`}
              position={{lat: b.lat, lng: b.lon}}
              icon={{
                url: makePinSvg("#f97316", "D"),
                scaledSize: new window.google.maps.Size(28, 36),
                anchor: new window.google.maps.Point(14, 36)
              }}
              title={b.bpName}
              onClick={() => setActiveInfo({bp: b, tabType: "drop"})}
            />
          ))}

        {/* ── BP info card overlay on marker click ── */}
        {activeInfo && (
          <OverlayView
            position={{lat: activeInfo.bp.lat, lng: activeInfo.bp.lon}}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <BpInfoCard
              bp={activeInfo.bp}
              tabType={activeInfo.tabType}
              onClose={() => setActiveInfo(null)}
              onSelect={() => {
                onSelectBp?.(activeInfo.bp, activeInfo.tabType);
                setActiveInfo(null);
              }}
            />
          </OverlayView>
        )}

        {/* ── Current pickup / drop BP markers (bullseye) ── */}
        {currentPickupBp && (
          <Marker
            position={currentPickupBp}
            icon={{
              url: makeCurrentBpSvg("#3b82f6"),
              scaledSize: new window.google.maps.Size(34, 34),
              anchor: new window.google.maps.Point(17, 17)
            }}
            title="Current Pickup Location"
          />
        )}
        {currentDropBp && (
          <Marker
            position={currentDropBp}
            icon={{
              url: makeCurrentBpSvg("#f97316"),
              scaledSize: new window.google.maps.Size(34, 34),
              anchor: new window.google.maps.Point(17, 17)
            }}
            title="Current Drop Location"
          />
        )}

        {/* ── Custom lat/lng draggable markers ── */}
        {newPickupPos &&
          !isNaN(newPickupPos.lat) &&
          !isNaN(newPickupPos.lng) && (
            <Marker
              position={newPickupPos}
              draggable
              onDragStart={() => setActiveCustomInfo(null)}
              onDragEnd={e => {
                if (e.latLng && onPickupPosDrag)
                  onPickupPosDrag({lat: e.latLng.lat(), lng: e.latLng.lng()});
              }}
              onClick={() =>
                setActiveCustomInfo({pos: newPickupPos, tabType: "pickup"})
              }
              icon={{
                url: makePinSvg("#3b82f6", "P"),
                scaledSize: new window.google.maps.Size(36, 44),
                anchor: new window.google.maps.Point(18, 44)
              }}
            />
          )}
        {newDropPos && !isNaN(newDropPos.lat) && !isNaN(newDropPos.lng) && (
          <Marker
            position={newDropPos}
            draggable
            onDragStart={() => setActiveCustomInfo(null)}
            onDragEnd={e => {
              if (e.latLng && onDropPosDrag)
                onDropPosDrag({lat: e.latLng.lat(), lng: e.latLng.lng()});
            }}
            onClick={() =>
              setActiveCustomInfo({pos: newDropPos, tabType: "drop"})
            }
            icon={{
              url: makePinSvg("#f97316", "D"),
              scaledSize: new window.google.maps.Size(36, 44),
              anchor: new window.google.maps.Point(18, 44)
            }}
          />
        )}
        {activeCustomInfo && (
          <OverlayView
            position={activeCustomInfo.pos}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <CustomPosCard
              pos={activeCustomInfo.pos}
              tabType={activeCustomInfo.tabType}
              onClose={() => setActiveCustomInfo(null)}
              onSearch={() => {
                onSearchFromPos?.(
                  activeCustomInfo.pos,
                  activeCustomInfo.tabType
                );
                setActiveCustomInfo(null);
              }}
            />
          </OverlayView>
        )}

        {/* ── Pick-from-map draggable pin ── */}
        {pinPickPos && (
          <Marker
            position={pinPickPos}
            draggable
            onDragEnd={e => {
              if (e.latLng && onPinDrag) {
                onPinDrag({lat: e.latLng.lat(), lng: e.latLng.lng()});
              }
            }}
            icon={{
              url: makePinSvg(pinPickColor, "📍"),
              scaledSize: new window.google.maps.Size(36, 44),
              anchor: new window.google.maps.Point(18, 44)
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const ChangePickupDropModal = ({
  data
}: {
  data: ChangePickupDropModalData;
}) => {
  const {
    client,
    items,
    userId,
    onClose,
    onRefresh,
    vehicleOptions: vehicleOptionsProp,
    boardingPointOptions
  } = data;
  const vehicleOptions = vehicleOptionsProp?.length
    ? vehicleOptionsProp
    : MOCK_VEHICLE_OPTIONS;

  const [vehiclesData, setVehiclesData] = useState<VehicleWithBps[]>([]);
  const [announcementVehicles, setAnnouncementVehicles] = useState<
    AnnouncementVehicle[]
  >([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [changeType, setChangeType] = useState<ChangeType>("both");
  const [activeTab, setActiveTab] = useState<string>("pickup");
  const [selectedVehicle, setSelectedVehicle] = useState<SelectOption | null>(
    vehicleOptions[0] ?? null
  );
  // When a vehicle has more than one active route of the same trip type
  // (e.g. an alternate-days pickup route), track which one is treated as
  // the "current" route for that trip type.
  const [selectedPickupRouteId, setSelectedPickupRouteId] = useState<
    string | null
  >(null);
  const [selectedDropRouteId, setSelectedDropRouteId] = useState<
    string | null
  >(null);

  const [pickupRoute, setPickupRoute] = useState<SelectOption | null>(null);
  const [pickupVehicle, setPickupVehicle] = useState<SelectOption | null>(null);
  const [pickupLocType, setPickupLocType] = useState<LocationType>("custom");
  const [pickupPoint, setPickupPoint] = useState<SelectOption | null>(null);
  const [pickupCustomLat, setPickupCustomLat] = useState("");
  const [pickupCustomLng, setPickupCustomLng] = useState("");

  const [dropRoute, setDropRoute] = useState<SelectOption | null>(null);
  const [dropVehicle, setDropVehicle] = useState<SelectOption | null>(null);
  const [dropLocType, setDropLocType] = useState<LocationType>("custom");
  const [dropPoint, setDropPoint] = useState<SelectOption | null>(null);
  const [dropCustomLat, setDropCustomLat] = useState("");
  const [dropCustomLng, setDropCustomLng] = useState("");

  const [pickupRefRoute, setPickupRefRoute] = useState<RefRoutePoint[]>([]);
  const [pickupBoardings, setPickupBoardings] = useState<RefBoarding[]>([]);
  const [dropRefRoute, setDropRefRoute] = useState<RefRoutePoint[]>([]);
  const [dropBoardings, setDropBoardings] = useState<RefBoarding[]>([]);

  // Refs updated synchronously in onChange handlers so in-flight API responses
  // can be discarded even if they resolve before React runs the effect cleanup.
  const activePickupRouteId = useRef<string | undefined>(undefined);
  const activeDropRouteId = useRef<string | undefined>(undefined);

  const [searchLat, setSearchLat] = useState("");
  const [searchLng, setSearchLng] = useState("");
  const [searchRadius, setSearchRadius] = useState(0.5);
  const [pinPickMode, setPinPickMode] = useState(false);
  const [pinPickPos, setPinPickPos] = useState<LatLng | null>(null);
  const [nearbyVehicles, setNearbyVehicles] = useState<NearbyVehicle[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAllVehicles, setShowAllVehicles] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const showPickup = changeType === "pickup" || changeType === "both";
  const showDrop = changeType === "drop" || changeType === "both";

  // API-driven: current pickup/drop from the selected vehicle's routes
  const selectedVehicleData = vehiclesData.find(
    v => v.id === selectedVehicle?.value
  );
  const pickupRoutesForVehicle: VehicleRoute[] =
    selectedVehicleData?.routes.filter(r => r.type === 0) ?? [];
  const dropRoutesForVehicle: VehicleRoute[] =
    selectedVehicleData?.routes.filter(r => r.type === 1) ?? [];
  const currentPickupRoute =
    pickupRoutesForVehicle.find(
      r => String(r.routeId) === selectedPickupRouteId
    ) ?? pickupRoutesForVehicle[0];
  const currentDropRoute =
    dropRoutesForVehicle.find(r => String(r.routeId) === selectedDropRouteId) ??
    dropRoutesForVehicle[0];

  // Fallback to items.vehicles when API data is not yet loaded
  const currentPickup = items?.vehicles?.find(
    v => v.type === 1 || v.type === 3
  );
  const currentDrop = items?.vehicles?.find(v => v.type === 2 || v.type === 3);

  // Current BP positions for map markers
  const currentPickupBp: LatLng | null =
    currentPickupRoute?.bpLat && currentPickupRoute?.bpLng
      ? {lat: currentPickupRoute.bpLat, lng: currentPickupRoute.bpLng}
      : null;
  const currentDropBp: LatLng | null =
    currentDropRoute?.bpLat && currentDropRoute?.bpLng
      ? {lat: currentDropRoute.bpLat, lng: currentDropRoute.bpLng}
      : null;

  // vehicleTripType: 1 = pickup only, 2 = drop only, 0 or 3 = pickup & drop.
  // A vehicle whose trip type excludes a leg should never offer it, even if
  // stale route data for that leg happens to be present.
  const vehicleTripType = selectedVehicleData?.vehicleTripType;
  const supportsPickup =
    vehicleTripType == null ||
    vehicleTripType === 0 ||
    vehicleTripType === 1 ||
    vehicleTripType === 3;
  const supportsDrop =
    vehicleTripType == null ||
    vehicleTripType === 0 ||
    vehicleTripType === 2 ||
    vehicleTripType === 3;

  // Once the vehiclesWithBps API has loaded for this vehicle, it is the
  // source of truth for which routes actually exist — don't let the stale
  // `items.vehicles` fallback keep showing a pickup/drop that isn't there.
  const hasPickup = selectedVehicleData
    ? supportsPickup && !!currentPickupRoute
    : !!currentPickup;
  const hasDrop = selectedVehicleData
    ? supportsDrop && !!currentDropRoute
    : !!currentDrop;
  // Nothing to change if neither pickup nor drop exists for this user.
  const canChange = hasPickup || hasDrop;

  // Reset the selected current-route when the selected vehicle changes so a
  // stale routeId from a previous vehicle never lingers.
  useEffect(() => {
    setSelectedPickupRouteId(
      selectedVehicleData?.routes.find(r => r.type === 0)?.routeId != null
        ? String(
            selectedVehicleData.routes.find(r => r.type === 0)!.routeId
          )
        : null
    );
    setSelectedDropRouteId(
      selectedVehicleData?.routes.find(r => r.type === 1)?.routeId != null
        ? String(
            selectedVehicleData.routes.find(r => r.type === 1)!.routeId
          )
        : null
    );
  }, [selectedVehicleData?.id]);

  // Auto-set changeType when vehicle data loads
  useEffect(() => {
    if (hasPickup && !hasDrop) {
      setChangeType("pickup");
      setActiveTab("pickup");
    } else if (!hasPickup && hasDrop) {
      setChangeType("drop");
      setActiveTab("drop");
    } else if (hasPickup && hasDrop) {
      setChangeType("both");
    }
  }, [hasPickup, hasDrop]);

  // Vehicle options for form dropdowns — from Announcement API
  const announcementVehicleOptions: SelectOption[] = announcementVehicles.map(
    v => ({value: v.trackerId, label: v.trackerName})
  );

  // Route options — filtered by whichever vehicle is selected in each form dropdown
  const pickupAnnouncementVehicle = announcementVehicles.find(
    v => v.trackerId === pickupVehicle?.value
  );
  const pickupRouteOptions: SelectOption[] =
    pickupAnnouncementVehicle?.routes
      .filter(r => r.routeId !== 0 && r.routeName && r.tripType === 0)
      .map(r => ({value: String(r.routeId), label: r.routeName!})) ?? [];

  const dropAnnouncementVehicle = announcementVehicles.find(
    v => v.trackerId === dropVehicle?.value
  );
  const dropRouteOptions: SelectOption[] =
    dropAnnouncementVehicle?.routes
      .filter(r => r.routeId !== 0 && r.routeName && r.tripType === 1)
      .map(r => ({value: String(r.routeId), label: r.routeName!})) ?? [];

  // Boarding point options for Existing Point selects — from reference route API
  const pickupBoardingOptions: SelectOption[] = pickupBoardings.map(b => ({
    value: String(b.bpId),
    label: b.bpName
  }));
  const dropBoardingOptions: SelectOption[] = dropBoardings.map(b => ({
    value: String(b.bpId),
    label: b.bpName
  }));

  const newPickupPos: LatLng | null =
    pickupLocType === "custom" && pickupCustomLat && pickupCustomLng
      ? {lat: parseFloat(pickupCustomLat), lng: parseFloat(pickupCustomLng)}
      : null;

  const newDropPos: LatLng | null =
    dropLocType === "custom" && dropCustomLat && dropCustomLng
      ? {lat: parseFloat(dropCustomLat), lng: parseFloat(dropCustomLng)}
      : null;

  const handleNearbyVehicleSelect = (v: NearbyVehicle) => {
    const vehicleOption: SelectOption = {
      value: v.trackerId,
      label: v.trackerName
    };
    const routeOption: SelectOption = {
      value: String(v.routeId),
      label: v.routeName
    };
    if (activeTab === "pickup") {
      setPickupVehicle(vehicleOption);
      setPickupRoute(routeOption);
      activePickupRouteId.current = routeOption.value;
      setPickupPoint(null);
      setPickupRefRoute([]);
      setPickupBoardings([]);
    } else {
      setDropVehicle(vehicleOption);
      setDropRoute(routeOption);
      activeDropRouteId.current = routeOption.value;
      setDropPoint(null);
      setDropRefRoute([]);
      setDropBoardings([]);
    }
  };

  const handleSearchVehicles = async () => {
    const lat = parseFloat(searchLat);
    const lng = parseFloat(searchLng);
    if (isNaN(lat) || isNaN(lng)) return;
    const tripType: 0 | 1 = activeTab === "pickup" ? 0 : 1;
    const radiusInMeters = Math.round(searchRadius * 1000);
    setIsSearching(true);
    const results = await userService.onFindVehiclesNearLocation(
      lat,
      lng,
      radiusInMeters,
      tripType
    );
    setNearbyVehicles(results);
    setIsSearching(false);
    setShowAllVehicles(false);
  };

  const [showReview, setShowReview] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const fetchVehicles = async () => {
      setIsLoadingVehicles(true);
      const list: VehicleWithBps[] =
        await userService.onGetVehiclesWithBps(userId);
      setVehiclesData(list);
      if (list.length > 0) {
        setSelectedVehicle({value: list[0].id, label: list[0].name});
        const pickupRoute = list[0].routes.find(r => r.type === 0);
        if (pickupRoute?.bpLat && pickupRoute?.bpLng) {
          setSearchLat(String(pickupRoute.bpLat));
          setSearchLng(String(pickupRoute.bpLng));
          setPickupCustomLat(String(pickupRoute.bpLat));
          setPickupCustomLng(String(pickupRoute.bpLng));
        }
        const dropRoute = list[0].routes.find(r => r.type === 1);
        if (dropRoute?.bpLat && dropRoute?.bpLng) {
          setDropCustomLat(String(dropRoute.bpLat));
          setDropCustomLng(String(dropRoute.bpLng));
        }
      }
      setIsLoadingVehicles(false);
    };
    fetchVehicles();
  }, [userId]);

  // Sync search location to the relevant current BP when tab, selected
  // vehicle, or the chosen current route changes
  useEffect(() => {
    const bp = activeTab === "pickup" ? currentPickupRoute : currentDropRoute;
    if (bp?.bpLat && bp?.bpLng) {
      setSearchLat(String(bp.bpLat));
      setSearchLng(String(bp.bpLng));
      setPinPickPos(null);
    }
  }, [
    activeTab,
    selectedVehicle?.value,
    currentPickupRoute?.routeId,
    currentDropRoute?.routeId
  ]);

  // Keep the custom-location lat/lng fields in step with whichever route is
  // selected as the current pickup/drop for this vehicle.
  useEffect(() => {
    if (currentPickupRoute?.bpLat && currentPickupRoute?.bpLng) {
      setPickupCustomLat(String(currentPickupRoute.bpLat));
      setPickupCustomLng(String(currentPickupRoute.bpLng));
    }
  }, [currentPickupRoute?.routeId]);

  useEffect(() => {
    if (currentDropRoute?.bpLat && currentDropRoute?.bpLng) {
      setDropCustomLat(String(currentDropRoute.bpLat));
      setDropCustomLng(String(currentDropRoute.bpLng));
    }
  }, [currentDropRoute?.routeId]);

  useEffect(() => {
    if (!client?.institutionId) return;
    AnnouncementService.onGetInstVehiclesandMsgs(
      client.institutionId as number
    ).then(result => {
      setAnnouncementVehicles(result?.VehicleDatas || []);
    });
  }, [client?.institutionId]);

  useEffect(() => {
    activePickupRouteId.current = pickupRoute?.value;
    if (!pickupRoute?.value) {
      setPickupRefRoute([]);
      setPickupBoardings([]);
      return;
    }
    const routeId = pickupRoute.value;
    liveService
      .getreferenceroute({routeId: Number(routeId)})
      .then((res: any) => {
        if (activePickupRouteId.current !== routeId) return;
        if (res?.data?.status === "success") {
          const d = res.data.data;
          setPickupRefRoute(
            (d?.refRouteDetails || []).map((p: any) => ({
              lat: p.lat,
              lng: p.lon,
              ti: p.ti
            }))
          );
          setPickupBoardings(d?.userBoardings || []);
        }
      });
  }, [pickupRoute?.value]);

  useEffect(() => {
    activeDropRouteId.current = dropRoute?.value;
    if (!dropRoute?.value) {
      setDropRefRoute([]);
      setDropBoardings([]);
      return;
    }
    const routeId = dropRoute.value;
    liveService
      .getreferenceroute({routeId: Number(routeId)})
      .then((res: any) => {
        if (activeDropRouteId.current !== routeId) return;
        if (res?.data?.status === "success") {
          const d = res.data.data;
          setDropRefRoute(
            (d?.refRouteDetails || []).map((p: any) => ({
              lat: p.lat,
              lng: p.lon,
              ti: p.ti
            }))
          );
          setDropBoardings(d?.userBoardings || []);
        }
      });
  }, [dropRoute?.value]);

  const handleConfirmClick = () => {
    const errors: string[] = [];

    if (showPickup) {
      if (!pickupVehicle) errors.push("Pickup: select a vehicle.");
      if (!pickupRoute) errors.push("Pickup: select a route.");
      const hasBp =
        pickupLocType === "custom"
          ? !!pickupCustomLat && !!pickupCustomLng
          : !!pickupPoint;
      if (!hasBp)
        errors.push(
          pickupLocType === "custom"
            ? "Pickup: enter lat & lng coordinates."
            : "Pickup: select a boarding point."
        );
    }

    if (showDrop) {
      if (!dropVehicle) errors.push("Drop: select a vehicle.");
      if (!dropRoute) errors.push("Drop: select a route.");
      const hasBp =
        dropLocType === "custom"
          ? !!dropCustomLat && !!dropCustomLng
          : !!dropPoint;
      if (!hasBp)
        errors.push(
          dropLocType === "custom"
            ? "Drop: enter lat & lng coordinates."
            : "Drop: select a boarding point."
        );
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors([]);
    setShowReview(true);
  };

  const handleSubmit = async () => {
    const mappingRequests: Array<{
      existingTrackerId: number;
      newTrackerId: number;
      vehicleTripType: 1 | 2;
      existingRouteId: number;
      newRouteId: number;
      newBpLat: number;
      newBpLng: number;
      newBpId?: number;
    }> = [];

    if (showPickup && pickupVehicle && pickupRoute) {
      let bpLat = 0;
      let bpLng = 0;
      let bpId: number | undefined;
      if (pickupLocType === "custom") {
        bpLat = parseFloat(pickupCustomLat) || 0;
        bpLng = parseFloat(pickupCustomLng) || 0;
      } else {
        const bp = pickupBoardings.find(
          b => String(b.bpId) === pickupPoint?.value
        );
        bpLat = bp?.lat ?? 0;
        bpLng = bp?.lon ?? 0;
        bpId = bp?.bpId;
      }
      mappingRequests.push({
        existingTrackerId: Number(
          currentPickup?.trackerid ?? selectedVehicleData?.id ?? 0
        ),
        newTrackerId: Number(pickupVehicle.value),
        vehicleTripType: 1,
        existingRouteId: Number(
          currentPickupRoute?.routeId ?? currentPickup?.routeId ?? 0
        ),
        newRouteId: Number(pickupRoute.value),
        newBpLat: bpLat,
        newBpLng: bpLng,
        ...(bpId !== undefined ? {newBpId: bpId} : {})
      });
    }

    if (showDrop && dropVehicle && dropRoute) {
      let bpLat = 0;
      let bpLng = 0;
      let bpId: number | undefined;
      if (dropLocType === "custom") {
        bpLat = parseFloat(dropCustomLat) || 0;
        bpLng = parseFloat(dropCustomLng) || 0;
      } else {
        const bp = dropBoardings.find(b => String(b.bpId) === dropPoint?.value);
        bpLat = bp?.lat ?? 0;
        bpLng = bp?.lon ?? 0;
        bpId = bp?.bpId;
      }
      mappingRequests.push({
        existingTrackerId: Number(
          currentDrop?.trackerid ?? selectedVehicleData?.id ?? 0
        ),
        newTrackerId: Number(dropVehicle.value),
        vehicleTripType: 2,
        existingRouteId: Number(
          currentDropRoute?.routeId ?? currentDrop?.routeId ?? 0
        ),
        newRouteId: Number(dropRoute.value),
        newBpLat: bpLat,
        newBpLng: bpLng,
        ...(bpId !== undefined ? {newBpId: bpId} : {})
      });
    }

    if (mappingRequests.length === 0) return;

    setSubmitError(null);
    setIsSaving(true);
    const result = await userService.onVehicleChange(userId, mappingRequests);
    setIsSaving(false);

    if (result.status === "success") {
      onRefresh?.();
      onClose();
    } else {
      setSubmitError(
        result.message || "Vehicle change failed. Please try again."
      );
    }
  };

  const visibleVehicles = showAllVehicles
    ? nearbyVehicles
    : nearbyVehicles.slice(0, 4);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-shrink-0 items-start justify-between border-b border-gray-100 px-6 py-4">
        <div>
          <h2 className="text-base font-bold text-gray-900">
            Change Pickup &amp; Drop Location
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Update user&apos;s pickup and drop location or assign to different
            routes.
          </p>
          {(vehiclesData.length > 1 ||
            (!vehiclesData.length && vehicleOptions.length > 1)) && (
            <div className="mt-2.5">
              <span className="text-md mb-1.5 block font-medium text-gray-500">
                Select Vehicle
              </span>
              {isLoadingVehicles ? (
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <svg
                    className="animate-spin h-3.5 w-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="#e5e7eb"
                      strokeWidth="3"
                    />
                    <path
                      d="M12 2a10 10 0 0 1 10 10"
                      stroke="#16a34a"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                  Loading vehicles…
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(vehiclesData.length
                    ? vehiclesData.map(v => ({value: v.id, label: v.name}))
                    : vehicleOptions
                  ).map(opt => {
                    const isSelected = selectedVehicle?.value === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSelectedVehicle(opt)}
                        className={`relative flex items-center gap-2 rounded-xl border px-3 py-1.5 transition-all ${
                          isSelected
                            ? "border-green-500 bg-green-50 shadow-sm"
                            : "border-gray-200 bg-white hover:border-green-300 hover:bg-gray-50"
                        }`}
                      >
                        {/* Bus icon */}
                        <div
                          className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg ${
                            isSelected ? "bg-green-100" : "bg-gray-100"
                          }`}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <rect
                              x="2"
                              y="7"
                              width="20"
                              height="12"
                              rx="3"
                              fill={isSelected ? "#16a34a" : "#9ca3af"}
                            />
                            <rect
                              x="5"
                              y="4"
                              width="14"
                              height="5"
                              rx="2"
                              fill={isSelected ? "#15803d" : "#6b7280"}
                            />
                            <circle
                              cx="7"
                              cy="19"
                              r="1.5"
                              fill={isSelected ? "#166534" : "#4b5563"}
                            />
                            <circle
                              cx="17"
                              cy="19"
                              r="1.5"
                              fill={isSelected ? "#166534" : "#4b5563"}
                            />
                          </svg>
                        </div>

                        {/* Vehicle name */}
                        <span
                          className={`max-w-[120px] truncate text-xs font-medium ${
                            isSelected ? "text-green-800" : "text-gray-700"
                          }`}
                          title={opt.label}
                        >
                          {opt.label}
                        </span>

                        {/* Selected check badge */}
                        {isSelected && (
                          <div className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-green-500 shadow">
                            <svg
                              width="8"
                              height="8"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M5 13l4 4L19 7"
                                stroke="white"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Current Pickup / Drop info cards ─────────────────────── */}
      <div className="flex flex-shrink-0 gap-4 border-b border-gray-100 px-6 py-3">
        {/* Pickup card */}
        {selectedVehicleData && !supportsPickup ? (
          <div className="flex flex-1 items-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="#9ca3af" strokeWidth="2" />
                <path
                  d="M6.5 6.5l11 11"
                  stroke="#9ca3af"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-semibold text-gray-500">
                Current Pickup
              </span>
              <span className="text-xs text-gray-400">
                {selectedVehicleData.name} isn&apos;t assigned for Pickup
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 items-start gap-3 rounded-xl border border-gray-200 px-4 py-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-50">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                  fill="#3b82f6"
                />
                <circle cx="12" cy="9" r="2.5" fill="white" />
              </svg>
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <span className="text-xs font-semibold text-blue-700">
                Current Pickup
              </span>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="font-medium text-gray-700">
                  {currentPickupRoute?.routeName ||
                    currentPickup?.routeName ||
                    "—"}
                </span>
                <span className="text-gray-300">|</span>
                <span>
                  {selectedVehicleData?.name ||
                    currentPickup?.trackerName ||
                    "—"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-800">
                  {currentPickupRoute?.bpName || currentPickup?.bpName || "—"}
                </span>
                {currentPickupRoute?.startTime &&
                  currentPickupRoute?.endTime && (
                    <span className="flex items-center gap-1 rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="#3b82f6"
                          strokeWidth="2"
                        />
                        <path
                          d="M12 7v5l3 3"
                          stroke="#3b82f6"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      {fmtTime(currentPickupRoute.startTime)} –{" "}
                      {fmtTime(currentPickupRoute.endTime)}
                    </span>
                  )}
              </div>
              {currentPickupRoute?.activeOnDays && (
                <div className="flex flex-wrap gap-1">
                  {currentPickupRoute.activeOnDays.split(",").map(day => (
                    <span
                      key={day}
                      className="rounded px-1.5 py-0.5 text-[9px] font-semibold"
                      style={{background: "#dbeafe", color: "#1d4ed8"}}
                    >
                      {day.trim()}
                    </span>
                  ))}
                </div>
              )}
              {pickupRoutesForVehicle.length > 1 && (
                <div className="mt-1.5">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Switch Route
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {pickupRoutesForVehicle.map(route => {
                      const isSelected =
                        String(route.routeId) === selectedPickupRouteId;
                      return (
                        <button
                          key={route.routeId}
                          type="button"
                          onClick={() =>
                            setSelectedPickupRouteId(String(route.routeId))
                          }
                          title={route.activeOnDays}
                          className={`rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                            isSelected
                              ? "border-blue-600 bg-blue-600 text-white"
                              : "border-gray-300 bg-white text-gray-600 hover:border-blue-400"
                          }`}
                        >
                          {route.routeName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {hasPickup && (
              <NeoButton
                size="sm"
                color="white"
                className="h-8 self-start rounded-lg border border-blue-600 px-3 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                onPress={() => {
                  setChangeType("pickup");
                  setActiveTab("pickup");
                }}
              >
                Change
              </NeoButton>
            )}
          </div>
        )}

        {/* Drop card */}
        {selectedVehicleData && !supportsDrop ? (
          <div className="flex flex-1 items-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="#9ca3af" strokeWidth="2" />
                <path
                  d="M6.5 6.5l11 11"
                  stroke="#9ca3af"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-semibold text-gray-500">
                Current Drop
              </span>
              <span className="text-xs text-gray-400">
                {selectedVehicleData.name} isn&apos;t assigned for Drop
              </span>
            </div>
          </div>
        ) : (
        <div className="flex flex-1 items-start gap-3 rounded-xl border border-gray-200 px-4 py-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-orange-50">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                fill="#f97316"
              />
              <circle cx="12" cy="9" r="2.5" fill="white" />
            </svg>
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <span className="text-xs font-semibold text-orange-600">
              Current Drop
            </span>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="font-medium text-gray-700">
                {currentDropRoute?.routeName || currentDrop?.routeName || "—"}
              </span>
              <span className="text-gray-300">|</span>
              <span>
                {selectedVehicleData?.name || currentDrop?.trackerName || "—"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">
                {currentDropRoute?.bpName || currentDrop?.bpName || "—"}
              </span>
              {currentDropRoute?.startTime && currentDropRoute?.endTime && (
                <span className="flex items-center gap-1 rounded-md bg-orange-50 px-1.5 py-0.5 text-[10px] font-medium text-orange-600">
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="#f97316"
                      strokeWidth="2"
                    />
                    <path
                      d="M12 7v5l3 3"
                      stroke="#f97316"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  {fmtTime(currentDropRoute.startTime)} –{" "}
                  {fmtTime(currentDropRoute.endTime)}
                </span>
              )}
            </div>
            {currentDropRoute?.activeOnDays && (
              <div className="flex flex-wrap gap-1">
                {currentDropRoute.activeOnDays.split(",").map(day => (
                  <span
                    key={day}
                    className="rounded px-1.5 py-0.5 text-[9px] font-semibold"
                    style={{background: "#ffedd5", color: "#c2410c"}}
                  >
                    {day.trim()}
                  </span>
                ))}
              </div>
            )}
            {dropRoutesForVehicle.length > 1 && (
              <div className="mt-1.5">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Switch Route
                </span>
                <div className="flex flex-wrap gap-1">
                  {dropRoutesForVehicle.map(route => {
                    const isSelected =
                      String(route.routeId) === selectedDropRouteId;
                    return (
                      <button
                        key={route.routeId}
                        type="button"
                        onClick={() =>
                          setSelectedDropRouteId(String(route.routeId))
                        }
                        title={route.activeOnDays}
                        className={`rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                          isSelected
                            ? "border-orange-500 bg-orange-500 text-white"
                            : "border-gray-300 bg-white text-gray-600 hover:border-orange-400"
                        }`}
                      >
                        {route.routeName}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {hasDrop && (
            <NeoButton
              size="sm"
              color="white"
              className="h-8 self-start rounded-lg border border-orange-500 px-3 text-xs font-semibold text-orange-600 hover:bg-orange-50"
              onPress={() => {
                setChangeType("drop");
                setActiveTab("drop");
              }}
            >
              Change
            </NeoButton>
          )}
        </div>
        )}
      </div>

      {/* ── Change-type radio row ─────────────────────────────────── */}
      <div className="flex flex-shrink-0 items-center gap-6 border-b border-gray-100 px-6 py-2.5">
        <span className="text-xs font-medium text-gray-700">
          What do you want to change?
        </span>
        {(["pickup", "drop", "both"] as ChangeType[])
          .filter(t => {
            if (t === "pickup") return hasPickup;
            if (t === "drop") return hasDrop;
            return hasPickup && hasDrop;
          })
          .map(t => (
            <label key={t} className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="changeType"
                value={t}
                checked={changeType === t}
                onChange={() => {
                  setChangeType(t);
                  setActiveTab(t === "drop" ? "drop" : "pickup");
                }}
                className="accent-blue-600"
              />
              <span
                className={`text-xs ${changeType === t ? "font-semibold text-blue-700" : "text-gray-600"}`}
              >
                {t === "pickup"
                  ? "Change Pickup Only"
                  : t === "drop"
                    ? "Change Drop Only"
                    : "Change Both Pickup & Drop"}
              </span>
            </label>
          ))}
      </div>

      {/* ── Main 3-column body ───────────────────────────────────── */}
      <div className="flex min-h-0 flex-1">
        {/* Left – Form panel */}
        <div className="flex w-56 flex-shrink-0 flex-col overflow-y-auto border-r border-gray-100 px-4 py-4">
          {/* Tabs (only for "both") */}
          {changeType === "both" ? (
            <Tabs
              selectedKey={activeTab}
              onSelectionChange={k => setActiveTab(k as string)}
              classNames={{
                tabList: "bg-gray-100 rounded-lg p-0.5 w-full gap-0.5",
                tab: "rounded-md h-7 text-xs data-[selected=true]:bg-white data-[selected=true]:shadow-sm",
                tabContent:
                  "text-gray-500 group-data-[selected=true]:text-gray-900 group-data-[selected=true]:font-semibold",
                cursor: "hidden"
              }}
              fullWidth
            >
              <Tab key="pickup" title="Pickup" />
              <Tab key="drop" title="Drop" />
            </Tabs>
          ) : (
            <div className="mb-1 rounded-lg bg-gray-100 py-1.5 text-center text-xs font-semibold text-gray-800">
              {changeType === "pickup" ? "Pickup" : "Drop"}
            </div>
          )}

          {/* Pickup form */}
          {(activeTab === "pickup" || changeType === "pickup") &&
            showPickup && (
              <div className="mt-3 flex flex-col gap-3">
                <div className="flex items-start gap-2">
                  <StepBadge number={1} color="#3b82f6" />
                  <SectionLabel color="#3b82f6">
                    Select New Pickup Route &amp; Vehicle
                  </SectionLabel>
                </div>
                <div>
                  <FieldLabel required>Vehicle</FieldLabel>
                  <Select
                    options={announcementVehicleOptions}
                    value={pickupVehicle}
                    onChange={v => {
                      setPickupVehicle(v);
                      setPickupRoute(null);
                      activePickupRouteId.current = undefined;
                      setPickupRefRoute([]);
                      setPickupBoardings([]);
                      setPickupPoint(null);
                    }}
                    placeholder="Select Vehicle"
                    styles={selectStyles}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : undefined
                    }
                    isClearable
                  />
                </div>
                <div>
                  <FieldLabel required>Route</FieldLabel>
                  <Select
                    options={pickupRouteOptions}
                    value={pickupRoute}
                    onChange={v => {
                      setPickupRoute(v);
                      activePickupRouteId.current = v?.value;
                      setPickupPoint(null);
                      if (!v) {
                        setPickupRefRoute([]);
                        setPickupBoardings([]);
                      }
                    }}
                    placeholder="Select Route"
                    styles={selectStyles}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : undefined
                    }
                    isClearable
                  />
                </div>
                <div className="flex items-start gap-2">
                  <StepBadge number={2} color="#3b82f6" />
                  <SectionLabel color="#3b82f6">
                    Select New Pickup Location
                  </SectionLabel>
                </div>
                <div className="flex gap-1">
                  {(["existing", "custom"] as LocationType[]).map(lt => (
                    <button
                      key={lt}
                      onClick={() => setPickupLocType(lt)}
                      className={`flex-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                        pickupLocType === lt
                          ? "border-blue-600 bg-blue-600 text-white"
                          : "border-gray-300 bg-white text-gray-600 hover:border-blue-400"
                      }`}
                    >
                      {lt === "existing" ? "Existing Point" : "Custom Location"}
                    </button>
                  ))}
                </div>
                {pickupLocType === "existing" ? (
                  <div>
                    <FieldLabel required>Pickup Point</FieldLabel>
                    <Select
                      options={pickupBoardingOptions}
                      value={pickupPoint}
                      onChange={setPickupPoint}
                      placeholder="Select Point"
                      styles={selectStyles}
                      menuPortalTarget={
                        typeof document !== "undefined"
                          ? document.body
                          : undefined
                      }
                      isClearable
                    />
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <div>
                      <FieldLabel>Latitude</FieldLabel>
                      <input
                        type="number"
                        step="0.0001"
                        value={pickupCustomLat}
                        onChange={e => setPickupCustomLat(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
                        placeholder="e.g. 11.0168"
                      />
                    </div>
                    <div>
                      <FieldLabel>Longitude</FieldLabel>
                      <input
                        type="number"
                        step="0.0001"
                        value={pickupCustomLng}
                        onChange={e => setPickupCustomLng(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
                        placeholder="e.g. 76.9558"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

          {/* Drop form */}
          {(activeTab === "drop" || changeType === "drop") && showDrop && (
            <div className="mt-3 flex flex-col gap-3">
              <div className="flex items-start gap-2">
                <StepBadge number={1} color="#f97316" />
                <SectionLabel color="#f97316">
                  Select New Drop Route &amp; Vehicle
                </SectionLabel>
              </div>
              <div>
                <FieldLabel required>Vehicle</FieldLabel>
                <Select
                  options={announcementVehicleOptions}
                  value={dropVehicle}
                  onChange={v => {
                    setDropVehicle(v);
                    setDropRoute(null);
                    activeDropRouteId.current = undefined;
                    setDropRefRoute([]);
                    setDropBoardings([]);
                    setDropPoint(null);
                  }}
                  placeholder="Select Vehicle"
                  styles={selectStyles}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : undefined
                  }
                  isClearable
                />
              </div>
              <div>
                <FieldLabel required>Route</FieldLabel>
                <Select
                  options={dropRouteOptions}
                  value={dropRoute}
                  onChange={v => {
                    setDropRoute(v);
                    activeDropRouteId.current = v?.value;
                    setDropPoint(null);
                    if (!v) {
                      setDropRefRoute([]);
                      setDropBoardings([]);
                    }
                  }}
                  placeholder="Select Route"
                  styles={selectStyles}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : undefined
                  }
                  isClearable
                />
              </div>
              <div className="flex items-start gap-2">
                <StepBadge number={2} color="#f97316" />
                <SectionLabel color="#f97316">
                  Select New Drop Location
                </SectionLabel>
              </div>
              <div className="flex gap-1">
                {(["existing", "custom"] as LocationType[]).map(lt => (
                  <button
                    key={lt}
                    onClick={() => setDropLocType(lt)}
                    className={`flex-1 rounded-md border px-1.5 py-0.5  text-[10px] font-medium transition-colors ${
                      dropLocType === lt
                        ? "border-orange-500 bg-orange-500 text-white"
                        : "border-gray-300 bg-white text-gray-600 hover:border-orange-400"
                    }`}
                  >
                    {lt === "existing" ? "Existing Point" : "Custom Location"}
                  </button>
                ))}
              </div>
              {dropLocType === "existing" ? (
                <div>
                  <FieldLabel required>Drop Point</FieldLabel>
                  <Select
                    options={dropBoardingOptions}
                    value={dropPoint}
                    onChange={setDropPoint}
                    placeholder="Select Point"
                    styles={selectStyles}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : undefined
                    }
                    isClearable
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div>
                    <FieldLabel>Latitude</FieldLabel>
                    <input
                      type="number"
                      step="0.0001"
                      value={dropCustomLat}
                      onChange={e => setDropCustomLat(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs focus:border-orange-500 focus:outline-none"
                      placeholder="e.g. 11.0231"
                    />
                  </div>
                  <div>
                    <FieldLabel>Longitude</FieldLabel>
                    <input
                      type="number"
                      step="0.0001"
                      value={dropCustomLng}
                      onChange={e => setDropCustomLng(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs focus:border-orange-500 focus:outline-none"
                      placeholder="e.g. 76.9821"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Info note */}
          {/* <div className="mt-auto pt-4">
            <div className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2">
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                className="mt-0.5 flex-shrink-0"
              >
                <circle cx="12" cy="12" r="10" fill="#3b82f6" />
                <path
                  d="M12 8v4m0 4h.01"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
              <p className="text-xs text-blue-700">
                New pickup and drop locations must be on the selected routes.
              </p>
            </div>
          </div> */}
        </div>

        {/* Center – Google Map (full height) */}
        <div className="relative flex-1 overflow-hidden">
          <MapSection
            showPickup={showPickup}
            showDrop={showDrop}
            newPickupPos={newPickupPos}
            newDropPos={newDropPos}
            pickupRefRoute={pickupRefRoute}
            dropRefRoute={dropRefRoute}
            pickupBoardings={pickupBoardings}
            dropBoardings={dropBoardings}
            currentPickupBp={currentPickupBp}
            currentDropBp={currentDropBp}
            initialCenter={currentPickupBp ?? currentDropBp}
            pinPickMode={pinPickMode}
            pinPickPos={pinPickPos}
            pinPickColor={activeTab === "pickup" ? "#3b82f6" : "#f97316"}
            onMapClick={pos => {
              setPinPickPos(pos);
              setSearchLat(pos.lat.toFixed(6));
              setSearchLng(pos.lng.toFixed(6));
            }}
            onPinDrag={pos => {
              setPinPickPos(pos);
              setSearchLat(pos.lat.toFixed(6));
              setSearchLng(pos.lng.toFixed(6));
            }}
            onSelectBp={(bp, tabType) => {
              const option = {value: String(bp.bpId), label: bp.bpName};
              if (tabType === "pickup") {
                setPickupPoint(option);
                setPickupLocType("existing");
              } else {
                setDropPoint(option);
                setDropLocType("existing");
              }
            }}
            onPickupPosDrag={pos => {
              setPickupCustomLat(pos.lat.toFixed(6));
              setPickupCustomLng(pos.lng.toFixed(6));
            }}
            onDropPosDrag={pos => {
              setDropCustomLat(pos.lat.toFixed(6));
              setDropCustomLng(pos.lng.toFixed(6));
            }}
            onSearchFromPos={async (pos, tabType) => {
              setActiveTab(tabType);
              setSearchLat(pos.lat.toFixed(6));
              setSearchLng(pos.lng.toFixed(6));
              const tripType: 0 | 1 = tabType === "pickup" ? 0 : 1;
              const radiusInMeters = Math.round(searchRadius * 1000);
              setIsSearching(true);
              const results = await userService.onFindVehiclesNearLocation(
                pos.lat,
                pos.lng,
                radiusInMeters,
                tripType
              );
              setNearbyVehicles(results);
              setIsSearching(false);
              setShowAllVehicles(false);
            }}
            onUseRoutePoint={(pos, tabType) => {
              if (tabType === "pickup") {
                setPickupLocType("custom");
                setPickupCustomLat(pos.lat.toFixed(6));
                setPickupCustomLng(pos.lng.toFixed(6));
              } else {
                setDropLocType("custom");
                setDropCustomLat(pos.lat.toFixed(6));
                setDropCustomLng(pos.lng.toFixed(6));
              }
            }}
          />

          {/* ── Review Changes overlay (shown on Confirm click) ───────── */}
          {showReview && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
              <div className="mx-6 w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                  <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"
                          stroke="#3b82f6"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">
                      Review Changes
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowReview(false)}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M18 6L6 18M6 6l12 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>

                {/* Changes summary */}
                <div className="flex flex-col gap-3 px-5 py-4">
                  {showPickup && (
                    <div className="flex items-stretch gap-3">
                      {/* Current */}
                      <div className="flex-1 rounded-xl border border-gray-200 p-3">
                        <div className="mb-1.5 flex items-center gap-1.5">
                          <div className="h-2 w-2 rounded-full bg-blue-600" />
                          <span className="text-xs font-semibold text-blue-700">
                            Pickup
                          </span>
                          <span className="ml-auto rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                            Current
                          </span>
                        </div>
                        <div className="text-xs font-medium text-gray-800">
                          {currentPickupRoute?.bpName ||
                            currentPickup?.bpName ||
                            "—"}
                        </div>
                        <div className="mt-0.5 flex gap-2 text-xs text-gray-500">
                          <span>
                            {currentPickupRoute?.routeName ||
                              currentPickup?.routeName ||
                              "—"}
                          </span>
                          <span className="text-gray-300">·</span>
                          <span>
                            {selectedVehicleData?.name ||
                              currentPickup?.trackerName ||
                              "—"}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-shrink-0 items-center">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M5 12h14M12 5l7 7-7 7"
                            stroke="#3b82f6"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>

                      {/* New */}
                      <div className="flex-1 rounded-xl border border-blue-200 bg-blue-50 p-3">
                        <div className="mb-1.5 flex items-center gap-1.5">
                          <div className="h-2 w-2 rounded-full bg-blue-500" />
                          <span className="text-xs font-semibold text-blue-700">
                            Pickup
                          </span>
                          <span className="ml-auto rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
                            New
                          </span>
                        </div>
                        {pickupRoute || pickupPoint ? (
                          <>
                            <div className="text-xs font-medium text-gray-800">
                              {pickupPoint?.label || "—"}
                            </div>
                            <div className="mt-0.5 flex gap-2 text-xs text-gray-500">
                              {pickupRoute && <span>{pickupRoute.label}</span>}
                              {pickupVehicle && (
                                <>
                                  <span className="text-gray-300">·</span>
                                  <span>{pickupVehicle.label}</span>
                                </>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="text-xs italic text-gray-400">
                            No changes selected
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {showDrop && (
                    <div className="flex items-stretch gap-3">
                      {/* Current */}
                      <div className="flex-1 rounded-xl border border-gray-200 p-3">
                        <div className="mb-1.5 flex items-center gap-1.5">
                          <div className="h-2 w-2 rounded-full bg-orange-500" />
                          <span className="text-xs font-semibold text-orange-600">
                            Drop
                          </span>
                          <span className="ml-auto rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                            Current
                          </span>
                        </div>
                        <div className="text-xs font-medium text-gray-800">
                          {currentDropRoute?.bpName ||
                            currentDrop?.bpName ||
                            "—"}
                        </div>
                        <div className="mt-0.5 flex gap-2 text-xs text-gray-500">
                          <span>
                            {currentDropRoute?.routeName ||
                              currentDrop?.routeName ||
                              "—"}
                          </span>
                          <span className="text-gray-300">·</span>
                          <span>
                            {selectedVehicleData?.name ||
                              currentDrop?.trackerName ||
                              "—"}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-shrink-0 items-center">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M5 12h14M12 5l7 7-7 7"
                            stroke="#f97316"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>

                      {/* New */}
                      <div className="flex-1 rounded-xl border border-orange-200 bg-orange-50 p-3">
                        <div className="mb-1.5 flex items-center gap-1.5">
                          <div className="h-2 w-2 rounded-full bg-orange-400" />
                          <span className="text-xs font-semibold text-orange-600">
                            Drop
                          </span>
                          <span className="ml-auto rounded bg-orange-100 px-1.5 py-0.5 text-xs text-orange-600">
                            New
                          </span>
                        </div>
                        {dropRoute || dropPoint ? (
                          <>
                            <div className="text-xs font-medium text-gray-800">
                              {dropPoint?.label || "—"}
                            </div>
                            <div className="mt-0.5 flex gap-2 text-xs text-gray-500">
                              {dropRoute && <span>{dropRoute.label}</span>}
                              {dropVehicle && (
                                <>
                                  <span className="text-gray-300">·</span>
                                  <span>{dropVehicle.label}</span>
                                </>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="text-xs italic text-gray-400">
                            No changes selected
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex flex-col gap-2 rounded-b-2xl border-t border-gray-100 bg-gray-50 px-5 py-3">
                  {submitError && (
                    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="mt-0.5 flex-shrink-0"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="#ef4444"
                          strokeWidth="2"
                        />
                        <line
                          x1="12"
                          y1="8"
                          x2="12"
                          y2="12"
                          stroke="#ef4444"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <circle cx="12" cy="16" r="1" fill="#ef4444" />
                      </svg>
                      {submitError}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="3"
                          y="11"
                          width="18"
                          height="11"
                          rx="2"
                          stroke="#9ca3af"
                          strokeWidth="2"
                        />
                        <path
                          d="M7 11V7a5 5 0 0 1 10 0v4"
                          stroke="#9ca3af"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      Changes will be applied after submission.
                    </div>
                    <div className="flex gap-2">
                      <NeoButton
                        color="white"
                        size="sm"
                        className="rounded-lg border border-gray-300 px-4 text-gray-700"
                        onPress={() => setShowReview(false)}
                      >
                        Edit
                      </NeoButton>
                      <NeoButton
                        color="black"
                        size="sm"
                        className="rounded-lg px-5"
                        isLoading={isSaving}
                        onPress={handleSubmit}
                      >
                        {!isSaving && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            className="mr-1"
                          >
                            <path
                              d="M5 13l4 4L19 7"
                              stroke="white"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                        Submit
                      </NeoButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right – Find Vehicles panel */}
        <div className="flex w-64 flex-shrink-0 flex-col overflow-y-auto border-l border-gray-100 px-4 py-4">
          <h4 className="mb-3 text-xs font-semibold text-gray-800">
            Find Vehicles Near Location
          </h4>

          <div className="mb-1 text-xs font-medium text-gray-600">
            Search Location
          </div>

          <div className="mb-2">
            <FieldLabel>Latitude</FieldLabel>
            <div className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5">
              <input
                type="number"
                step="0.0001"
                value={searchLat}
                onChange={e => setSearchLat(e.target.value)}
                className="flex-1 bg-transparent text-xs outline-none"
              />
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2v4M12 18v4M2 12h4M18 12h4"
                  stroke="#9ca3af"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                  stroke="#9ca3af"
                  strokeWidth="2"
                />
              </svg>
            </div>
          </div>

          <div className="mb-3">
            <FieldLabel>Longitude</FieldLabel>
            <div className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5">
              <input
                type="number"
                step="0.0001"
                value={searchLng}
                onChange={e => setSearchLng(e.target.value)}
                className="flex-1 bg-transparent text-xs outline-none"
              />
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2v4M12 18v4M2 12h4M18 12h4"
                  stroke="#9ca3af"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                  stroke="#9ca3af"
                  strokeWidth="2"
                />
              </svg>
            </div>
          </div>

          {/* Pick from map toggle */}
          <div className="mb-3">
            <button
              type="button"
              onClick={() => {
                setPinPickMode(m => !m);
                if (pinPickMode) setPinPickPos(null);
              }}
              className="flex w-full items-center justify-center gap-2 rounded-xl border py-2 text-xs font-semibold transition-colors"
              style={
                pinPickMode
                  ? {
                      background:
                        activeTab === "pickup" ? "#eff6ff" : "#fff7ed",
                      borderColor:
                        activeTab === "pickup" ? "#3b82f6" : "#f97316",
                      color: activeTab === "pickup" ? "#2563eb" : "#ea580c"
                    }
                  : {
                      background: "#f9fafb",
                      borderColor: "#e5e7eb",
                      color: "#374151"
                    }
              }
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                  fill={
                    pinPickMode
                      ? activeTab === "pickup"
                        ? "#3b82f6"
                        : "#f97316"
                      : "#9ca3af"
                  }
                />
                <circle cx="12" cy="9" r="2.5" fill="white" />
              </svg>
              {pinPickMode ? "Cancel Pin Pick" : "Pick from Map"}
            </button>
            {pinPickMode && (
              <p className="mt-1.5 text-center text-[10px] text-gray-400">
                Click anywhere on the map to place a pin · Drag to adjust
              </p>
            )}
          </div>

          {/* Radius slider */}
          <div className="mb-4">
            <FieldLabel>Search Radius</FieldLabel>
            <div
              className="mb-2 flex items-center justify-center rounded-xl border py-2"
              style={{
                borderColor: activeTab === "pickup" ? "#bfdbfe" : "#fed7aa",
                background: activeTab === "pickup" ? "#eff6ff" : "#fff7ed"
              }}
            >
              <span
                className="text-sm font-bold"
                style={{color: activeTab === "pickup" ? "#2563eb" : "#ea580c"}}
              >
                {searchRadius.toFixed(1)} km
              </span>
            </div>
            <div className="px-1">
              <NeoSlider
                minValue={0.1}
                maxValue={1}
                step={0.1}
                value={searchRadius}
                onChange={v => setSearchRadius(v as number)}
                color="warning"
                size="sm"
                className="w-full"
              />
              <div className="mt-1 flex justify-between text-xs text-gray-400">
                <span>1 km</span>
                <span>0.5 km</span>
                <span>0.1 km</span>
              </div>
              <div className="mt-0.5 text-center text-xs text-gray-400">
                (Min: 0.1 km · Max: 1 km)
              </div>
            </div>
          </div>

          {/* Search button */}
          <button
            onClick={handleSearchVehicles}
            disabled={isSearching}
            className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{background: activeTab === "pickup" ? "#3b82f6" : "#f97316"}}
          >
            {isSearching ? (
              <svg
                className="animate-spin h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="white"
                  strokeWidth="3"
                  strokeOpacity="0.3"
                />
                <path
                  d="M12 2a10 10 0 0 1 10 10"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="white" strokeWidth="2" />
                <path
                  d="M21 21l-4.35-4.35"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {isSearching ? "Searching…" : "Search Vehicles"}
          </button>

          {/* Vehicles list */}
          {nearbyVehicles.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="mb-1 text-xs font-semibold text-gray-700">
                Vehicles Found ({nearbyVehicles.length})
              </div>
              {visibleVehicles.map(v => {
                const isSelected =
                  activeTab === "pickup"
                    ? pickupVehicle?.value === v.trackerId &&
                      pickupRoute?.value === String(v.routeId)
                    : dropVehicle?.value === v.trackerId &&
                      dropRoute?.value === String(v.routeId);
                const accentColor =
                  activeTab === "pickup" ? "#3b82f6" : "#f97316";
                return (
                  <div
                    key={v.trackerId + v.routeId}
                    onClick={() => handleNearbyVehicleSelect(v)}
                    className="flex cursor-pointer items-center gap-2 rounded-xl border p-2 transition-colors"
                    style={
                      isSelected
                        ? {
                            borderColor: accentColor,
                            background:
                              activeTab === "pickup" ? "#eff6ff" : "#fff7ed"
                          }
                        : {borderColor: "#f3f4f6"}
                    }
                  >
                    <div
                      className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg"
                      style={{
                        background: isSelected
                          ? activeTab === "pickup"
                            ? "#dbeafe"
                            : "#ffedd5"
                          : "#f3f4f6"
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="2"
                          y="7"
                          width="20"
                          height="12"
                          rx="3"
                          fill={isSelected ? accentColor : "#6b7280"}
                        />
                        <rect
                          x="5"
                          y="4"
                          width="14"
                          height="5"
                          rx="2"
                          fill={isSelected ? accentColor : "#4b5563"}
                        />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-xs font-semibold"
                        style={{color: isSelected ? accentColor : "#1f2937"}}
                      >
                        {v.trackerName}
                      </div>
                      <div className="truncate text-xs text-gray-500">
                        {v.routeName}
                      </div>
                      <div className="text-xs text-gray-400">{v.regNo}</div>
                    </div>
                    <div className="flex flex-shrink-0 flex-col items-end gap-1">
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M9 18l6-6-6-6"
                          stroke={isSelected ? accentColor : "#9ca3af"}
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                );
              })}
              {nearbyVehicles.length > 4 && (
                <button
                  onClick={() => setShowAllVehicles(!showAllVehicles)}
                  className="mt-1 w-full rounded-xl border border-gray-200 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  {showAllVehicles ? "Show Less" : "View More Vehicles"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <div className="flex flex-shrink-0 items-center justify-between border-t border-gray-100 px-6 py-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="11"
              width="18"
              height="11"
              rx="2"
              stroke="#9ca3af"
              strokeWidth="2"
            />
            <path
              d="M7 11V7a5 5 0 0 1 10 0v4"
              stroke="#9ca3af"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Changes will be applied after confirmation.
        </div>
        <div className="flex flex-col items-end gap-2">
          {validationErrors.length > 0 && (
            <div className="flex flex-col gap-0.5 rounded-xl border border-red-200 bg-red-50 px-3 py-2">
              {validationErrors.map((e, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 text-xs text-red-600"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="#ef4444"
                      strokeWidth="2"
                    />
                    <path
                      d="M12 8v4M12 16h.01"
                      stroke="#ef4444"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  {e}
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3">
            <NeoButton
              color="white"
              size="md"
              className="rounded-xl border border-gray-300 px-5 text-gray-700"
              onPress={onClose}
            >
              Cancel
            </NeoButton>
            <NeoButton
              color="black"
              size="md"
              className="rounded-xl px-5"
              isDisabled={!canChange}
              onPress={handleConfirmClick}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                className="mr-1"
              >
                <path
                  d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Confirm All Changes
            </NeoButton>
          </div>
        </div>
      </div>
    </div>
  );
};
